import { FC } from 'react';

import { usePostFileUpload } from '@/hooks';
import { Post } from '@/types';
import { Image, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

import { SimpleSpinner } from '@/components/common';

interface PostImageProps {
  isRecent?: boolean;
  post: Post;
}

const PostImage: FC<PostImageProps> = ({ isRecent, post }) => {
  const {
    fileInputRef,
    imagePreview,
    handleFileUploadClick,
    uploadAndAttachImage,
    isLoading,
    isHovered,
    setIsHovered,
    deleteAttachedImage,
  } = usePostFileUpload(post);

  return (
    <div
      className='flex w-full min-h-20 max-h-[400px] relative'
      aria-label='add-file-button'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isLoading && <SimpleSpinner />}

      {!!imagePreview && (
        <div
          className={cn(
            'absolute top-4 right-4 md:top-0 md:right-0 z-10 flex items-center justify-center',
            'size-11 [&_svg]:size-6 rounded-full bg-black/60 md:bg-none md:rounded-none md:size-full md:hidden',
            { 'md:inset-0 md:bg-black/40 md:flex': isHovered }
          )}
        >
          <div
            className={cn(
              'flex items-center justify-center gap-2.5 p-2.5 cursor-pointer rounded-full size-10 [&_svg]:size-8',
              'text-white font-semibold whitespace-nowrap'
            )}
            onClick={deleteAttachedImage}
          >
            <div>
              <Trash2 />
            </div>{' '}
            <span className={'hidden md:block'}>Remove image</span>
          </div>
        </div>
      )}

      <div
        onClick={handleFileUploadClick}
        className={cn(
          'flex flex-col gap-2 justify-center items-center max-h-full w-full overflow-hidden rounded-lg ',
          {
            'border-2 border-dashed border-light-grey': !imagePreview,
            'cursor-pointer': !isRecent,
          }
        )}
      >
        {imagePreview ? (
          <img
            src={imagePreview}
            alt='Uploaded preview'
            className='size-full object-contain rounded-lg'
          />
        ) : (
          <div className={'flex items-center gap-4 [&>svg]:size-[32px]'}>
            <Image />
            <p className='text-sm font-semibold'>Add an image</p>
          </div>
        )}
      </div>
      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={uploadAndAttachImage}
        accept={'image/jpeg,image/jpg,image/png'}
      />
    </div>
  );
};

export default PostImage;
